import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Radio,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { explainFunctionError, functionErrorResponse } from "@how/app-kit";
import { ConfirmActionModal } from "@how/ui";
import { supabase } from "../../lib/supabase";
import { semanticColor } from "@how/design-system";

interface TwilioConnection {
  readonly id: string;
  readonly status: "pending" | "connected" | "error" | "revoked";
  readonly accountSid: string | null;
  readonly provisionedByDcx: boolean;
  readonly from: string | null;
  readonly messagingServiceSid: string | null;
  readonly suspended: boolean;
  readonly lastError: string | null;
}

function useTwilioStatus(tenantId: string) {
  return useQuery({
    queryKey: ["platform-twilio", tenantId],
    queryFn: async (): Promise<TwilioConnection | null> => {
      const { data, error } = await supabase.rpc("platform_twilio_status", {
        p_tenant_id: tenantId,
      });
      if (error) throw error;
      return (data as unknown as { connection: TwilioConnection | null }).connection;
    },
  });
}

type Action =
  | { readonly action: "create" }
  | { readonly action: "buy_number"; readonly area_code: string; readonly number?: string }
  | { readonly action: "suspend" }
  | { readonly action: "reactivate" }
  | { readonly action: "release_number" };

function useTwilioAction(tenantId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: Action) => {
      const result = await supabase.functions.invoke("twilio-subaccounts", {
        body: { tenant_id: tenantId, ...input },
      });
      if (result.error) {
        throw await explainFunctionError(result.error, functionErrorResponse(result.error));
      }
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["platform-twilio", tenantId] });
    },
  });
}

/** A number Twilio offers, as `search_numbers` lists it. */
interface AvailableNumber {
  readonly number: string;
  readonly locality: string | null;
  readonly region: string | null;
}

/** +19495554673 as +1 (949) 555-4673, so a person can read and compare them. */
export function formatUsNumber(e164: string): string {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return m === null ? e164 : `+1 (${m[1] ?? ""}) ${m[2] ?? ""}-${m[3] ?? ""}`;
}

/**
 * Choose the church's number (owner, 2026-10-02): search an area code, optionally for digits it should contain, and
 * pick from up to 20, each with where it is. Buying asks first, in the parent's confirmation.
 */
function NumberPicker({
  tenantId,
  onBuy,
}: {
  readonly tenantId: string;
  readonly onBuy: (areaCode: string, number: string) => void;
}) {
  const [areaCode, setAreaCode] = useState("");
  const [contains, setContains] = useState("");
  const [chosen, setChosen] = useState<string | null>(null);
  const search = useMutation({
    mutationFn: async (): Promise<AvailableNumber[]> => {
      const result = await supabase.functions.invoke<{ numbers: AvailableNumber[] }>(
        "twilio-subaccounts",
        {
          body: {
            tenant_id: tenantId,
            action: "search_numbers",
            area_code: areaCode,
            ...(contains === "" ? {} : { contains }),
          },
        },
      );
      if (result.error) {
        throw await explainFunctionError(result.error, functionErrorResponse(result.error));
      }
      return result.data?.numbers ?? [];
    },
    onSuccess: () => {
      setChosen(null);
    },
  });
  const validArea = /^[2-9][0-9]{2}$/.test(areaCode);
  const numbers = search.data ?? null;
  return (
    <Stack gap="xs">
      <Group align="flex-end" gap="xs">
        <TextInput
          label="Area code"
          placeholder="949"
          maxLength={3}
          w={110}
          value={areaCode}
          onChange={(event) => {
            setAreaCode(event.currentTarget.value.replace(/\D/g, ""));
          }}
        />
        <TextInput
          label="Contains (optional)"
          placeholder="4673"
          description="Digits the number should include"
          maxLength={7}
          w={190}
          value={contains}
          onChange={(event) => {
            setContains(event.currentTarget.value.replace(/\D/g, ""));
          }}
        />
        <Button
          size="xs"
          variant="default"
          disabled={!validArea}
          loading={search.isPending}
          onClick={() => {
            search.mutate();
          }}
        >
          Find numbers
        </Button>
      </Group>
      {search.isError ? (
        <Alert color={semanticColor("danger")} variant="light">
          {search.error.message}
        </Alert>
      ) : null}
      {numbers === null ? null : numbers.length === 0 ? (
        <Text size="sm" c="dimmed">
          Twilio has no texting numbers in {areaCode}
          {contains === "" ? "" : ` containing ${contains}`} right now. Try a neighbouring area code
          or fewer digits.
        </Text>
      ) : (
        <Radio.Group
          label={`${String(numbers.length)} available in ${areaCode}`}
          value={chosen}
          onChange={setChosen}
        >
          <Stack gap={6} mt={6}>
            {numbers.map((n) => (
              <Radio
                key={n.number}
                value={n.number}
                label={`${formatUsNumber(n.number)}${
                  n.locality === null
                    ? ""
                    : ` · ${n.locality}${n.region === null ? "" : `, ${n.region}`}`
                }`}
              />
            ))}
          </Stack>
        </Radio.Group>
      )}
      {chosen === null ? null : (
        <Group>
          <Button
            size="xs"
            onClick={() => {
              onBuy(areaCode, chosen);
            }}
          >
            Buy {formatUsNumber(chosen)}
          </Button>
        </Group>
      )}
    </Stack>
  );
}

const STATUS: Record<TwilioConnection["status"], { label: string; color: string }> = {
  pending: { label: "Not yet verified", color: "orange" },
  connected: { label: "Connected", color: "teal" },
  error: { label: "In error", color: "red" },
  revoked: { label: "Disconnected", color: "gray" },
};

/** What each confirmation says: the action, and what it costs or undoes. */
function copyFor(
  action: Action,
  from: string | null,
): { title: string; message: string; confirm: string } {
  switch (action.action) {
    case "create":
      return {
        title: "Create a Twilio subaccount",
        message:
          "Creates the church's subaccount under DCX's master Twilio account. Its usage is billed to DCX.",
        confirm: "Create",
      };
    case "buy_number":
      return {
        title: "Buy a number",
        message:
          action.number === undefined
            ? `Buys the first texting number Twilio has in area code ${action.area_code}, about $1.15 a month on DCX's bill, and points its replies at HoW.`
            : `Buys ${formatUsNumber(action.number)}, about $1.15 a month on DCX's bill, and points its replies at HoW.`,
        confirm: action.number === undefined ? "Buy" : `Buy ${formatUsNumber(action.number)}`,
      };
    case "release_number":
      return {
        title: `Release ${from ?? "this number"}?`,
        message: `Gives ${from ?? "the number"} back to Twilio and removes it from the church. Its monthly charge stops; texts to it stop reaching the church, and Twilio may give it to someone else, so it can't be taken back.`,
        confirm: "Release number",
      };
    case "suspend":
      return {
        title: "Suspend this subaccount",
        message:
          "Twilio stops everything on the subaccount: the church's texts are not sent until it is reactivated.",
        confirm: "Suspend",
      };
    case "reactivate":
      return {
        title: "Reactivate this subaccount",
        message: "Twilio reopens the subaccount; the next text verifies the connection again.",
        confirm: "Reactivate",
      };
  }
}

/**
 * A church's Twilio subaccount (ADR 0010, ADR 0048): DCX creates it under the master account, buys its number, and
 * suspends or reactivates it. Texts need an approved A2P 10DLC campaign on the number, attached in Twilio's console.
 */
export function TwilioCard({
  tenantId,
  canManage,
}: {
  readonly tenantId: string;
  readonly canManage: boolean;
}) {
  const status = useTwilioStatus(tenantId);
  const act = useTwilioAction(tenantId);
  const [confirm, setConfirm] = useState<Action | null>(null);
  const connection = status.data ?? null;

  return (
    <Card>
      <Stack gap="sm">
        <Group justify="space-between">
          <Title order={4}>Text messaging (Twilio)</Title>
          {connection === null ? null : (
            <Badge
              variant="light"
              color={connection.suspended ? "red" : STATUS[connection.status].color}
            >
              {connection.suspended ? "Suspended by DCX" : STATUS[connection.status].label}
            </Badge>
          )}
        </Group>
        {status.isPending ? (
          <Text size="sm" c="dimmed">
            Loading…
          </Text>
        ) : status.isError ? (
          <Alert color="red" variant="light">
            {status.error.message}
          </Alert>
        ) : connection === null ? (
          <Text size="sm" c="dimmed">
            No Twilio subaccount yet. DCX creates one per church under the master account; usage is
            billed to DCX and rebilled.
          </Text>
        ) : (
          <Stack gap={4}>
            <Text size="sm">
              Subaccount {connection.accountSid ?? "unknown"}
              {connection.provisionedByDcx ? "" : " (entered by hand)"}
            </Text>
            <Text size="sm">
              {connection.from !== null
                ? `Texts from ${connection.from}`
                : connection.messagingServiceSid !== null
                  ? `Texts through messaging service ${connection.messagingServiceSid}`
                  : "No number yet"}
            </Text>
            {connection.lastError === null ? null : (
              <Text size="sm" c="red">
                {connection.lastError}
              </Text>
            )}
            <Text size="xs" c="dimmed">
              A number texts only once an approved A2P 10DLC campaign is attached to it in
              Twilio&apos;s console.
            </Text>
          </Stack>
        )}
        {act.error ? (
          <Alert color="red" variant="light">
            {act.error.message}
          </Alert>
        ) : null}
        {!canManage || status.isPending || status.isError ? null : connection === null ? (
          <Group>
            <Button
              size="xs"
              onClick={() => {
                setConfirm({ action: "create" });
              }}
            >
              Create subaccount
            </Button>
          </Group>
        ) : (
          <Group align="flex-end">
            {connection.from === null && connection.messagingServiceSid === null ? (
              <NumberPicker
                tenantId={tenantId}
                onBuy={(area, number) => {
                  setConfirm({ action: "buy_number", area_code: area, number });
                }}
              />
            ) : null}
            {connection.from === null ? null : (
              <Button
                size="xs"
                variant="subtle"
                color={semanticColor("danger")}
                onClick={() => {
                  setConfirm({ action: "release_number" });
                }}
              >
                Release {connection.from}
              </Button>
            )}
            <Button
              size="xs"
              variant="subtle"
              color={semanticColor(connection.suspended ? "info" : "danger")}
              onClick={() => {
                setConfirm({ action: connection.suspended ? "reactivate" : "suspend" });
              }}
            >
              {connection.suspended ? "Reactivate" : "Suspend"}
            </Button>
          </Group>
        )}
      </Stack>
      <ConfirmActionModal
        opened={confirm !== null}
        onClose={() => {
          setConfirm(null);
        }}
        title={confirm === null ? "" : copyFor(confirm, connection?.from ?? null).title}
        message={confirm === null ? "" : copyFor(confirm, connection?.from ?? null).message}
        confirmLabel={confirm === null ? "" : copyFor(confirm, connection?.from ?? null).confirm}
        danger={confirm?.action === "suspend" || confirm?.action === "release_number"}
        loading={act.isPending}
        onConfirm={async () => {
          if (confirm !== null) await act.mutateAsync(confirm).catch(() => undefined);
          setConfirm(null);
        }}
      />
    </Card>
  );
}
